import React, { useState, useEffect, useCallback } from 'react';
import { 
  Globe, 
  FileText, 
  RefreshCw, 
  AlertCircle,
  Activity,
  TrendingUp,
  CheckCircle,
  Clock
} from 'lucide-react';
import { LoadingSpinner } from 'components/common';
import api from '../../../../services/api';

const ReelunetDashboard = ({ action, refreshKey = 0 }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/automacoes/dashboard', {
        params: { action }
      });
      const payload = response?.data?.data ?? response.data;
      setDashboardData(payload);
    } catch (err) {
      console.error('Erro ao carregar dashboard Reelunet:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [action]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData, refreshKey]);

  if (loading) {
    return (
      <LoadingSpinner
        fullScreen={false}
        className="min-h-[240px] w-full"
        cardClassName="max-w-md w-full"
        label="Carregando dashboard Reelunet"
        subtitle="Aguarde enquanto atualizamos os dados."
      />
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-[color:var(--text-primary)] mb-2">Erro ao carregar dados</h3>
        <p className="text-[color:var(--text-muted)] mb-4">{error}</p>
        <button 
          onClick={fetchDashboardData}
          className="inline-flex items-center px-4 py-2 bg-[color:var(--accent)] text-white rounded-md hover:opacity-90"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cabeçalho específico */}
      <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)] p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
              <Globe className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[color:var(--text-primary)]">Reelunet</h1>
              <p className="text-[color:var(--text-muted)]">Dashboard de automações da Reelunet - Assinatura de Processos</p>
            </div>
          </div>
        </div>
      </div>

      {/* Dados Gerais */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-[color:var(--text-primary)] mb-4 flex items-center">
          <Activity className="h-5 w-5 mr-2 text-blue-600" />
          Dados Gerais - Reelunet
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total de OS Finalizadas */}
          <div className="bg-[color:var(--surface)] p-6 rounded-lg border border-[color:var(--border)]">
            <div className="flex items-center">
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">Total de OS Finalizadas</p>
                <p className="text-2xl font-bold text-[color:var(--text-primary)]">
                  {dashboardData?.[0]?.AUTOMACAO_OS_REELUNET?.TOTAIS?.FINALIZADAS_AUTOMACAO || 0}
                </p>
              </div>
            </div>
          </div>

          {/* FTTH Total */}
          <div className="bg-[color:var(--surface)] p-6 rounded-lg border border-[color:var(--border)]">
            <div className="flex items-center">
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                <Activity className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">FTTH Total</p>
                <p className="text-2xl font-bold text-[color:var(--text-primary)]">
                  {dashboardData?.[0]?.AUTOMACAO_OS_REELUNET?.TOTAIS?.FTTH || 0}
                </p>
              </div>
            </div>
          </div>

          {/* CABO Total */}
          <div className="bg-[color:var(--surface)] p-6 rounded-lg border border-[color:var(--border)]">
            <div className="flex items-center">
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                <TrendingUp className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">CABO Total</p>
                <p className="text-2xl font-bold text-[color:var(--text-primary)]">
                  {dashboardData?.[0]?.AUTOMACAO_OS_REELUNET?.TOTAIS?.CABO || 0}
                </p>
              </div>
            </div>
          </div>

          {/* RADIO Total */}
          <div className="bg-[color:var(--surface)] p-6 rounded-lg border border-[color:var(--border)]">
            <div className="flex items-center">
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                <Globe className="h-6 w-6 text-[color:var(--accent)]" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">RADIO Total</p>
                <p className="text-2xl font-bold text-[color:var(--text-primary)]">
                  {dashboardData?.[0]?.AUTOMACAO_OS_REELUNET?.TOTAIS?.RADIO || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dados Mensais */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-[color:var(--text-primary)] mb-4 flex items-center">
          <Clock className="h-5 w-5 mr-2 text-green-600" />
          Dados Mensais - Reelunet ({dashboardData?.[0]?.AUTOMACAO_OS_REELUNET?.ULTIMO_MES?.MES || 'N/A'})
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* OSs Finalizadas no Último Mês */}
          <div className="bg-[color:var(--surface)] p-6 rounded-lg border border-[color:var(--border)]">
            <div className="flex items-center">
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">OSs Finalizadas no Mês</p>
                <p className="text-2xl font-bold text-[color:var(--text-primary)]">
                  {dashboardData?.[0]?.AUTOMACAO_OS_REELUNET?.ULTIMO_MES?.FINALIZADAS_AUTOMACAO || 0}
                </p>
              </div>
            </div>
          </div>

          {/* FTTH no Mês */}
          <div className="bg-[color:var(--surface)] p-6 rounded-lg border border-[color:var(--border)]">
            <div className="flex items-center">
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                <Activity className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">FTTH no Mês</p>
                <p className="text-2xl font-bold text-[color:var(--text-primary)]">
                  {dashboardData?.[0]?.AUTOMACAO_OS_REELUNET?.ULTIMO_MES?.FTTH || 0}
                </p>
              </div>
            </div>
          </div>

          {/* CABO no Mês */}
          <div className="bg-[color:var(--surface)] p-6 rounded-lg border border-[color:var(--border)]">
            <div className="flex items-center">
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                <TrendingUp className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">CABO no Mês</p>
                <p className="text-2xl font-bold text-[color:var(--text-primary)]">
                  {dashboardData?.[0]?.AUTOMACAO_OS_REELUNET?.ULTIMO_MES?.CABO || 0}
                </p>
              </div>
            </div>
          </div>

          {/* RADIO no Mês */}
          <div className="bg-[color:var(--surface)] p-6 rounded-lg border border-[color:var(--border)]">
            <div className="flex items-center">
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                <Globe className="h-6 w-6 text-[color:var(--accent)]" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">RADIO no Mês</p>
                <p className="text-2xl font-bold text-[color:var(--text-primary)]">
                  {dashboardData?.[0]?.AUTOMACAO_OS_REELUNET?.ULTIMO_MES?.RADIO || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReelunetDashboard;
